import React from "react";
import { useSearchParams } from "react-router-dom";
import T from "../styles/tokens";
import { PFLogo, Ic } from "../components/UI";

export default function Confirmation() {
  const [params] = useSearchParams();
  const status = params.get("status");
  const flow = params.get("flow") || "onboarding";
  const clientName = params.get("client") || "your organization";
  const template = params.get("template") || "";
  const errors = parseInt(params.get("errors") || "0", 10);

  const isSuccess = status === "success";
  const isOffboarding = flow === "offboarding";

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${T.indigo} 0%, ${T.purple} 50%, ${T.indigo} 100%)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="pf-public-shell" style={{ maxWidth: 540, margin: "0 auto", padding: "40px 24px", textAlign: "center" }}>
        {/* Logo */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 36 }}>
          <PFLogo size={36} />
          <span style={{ color: T.white, fontSize: 16, fontWeight: 700, fontFamily: T.font }}>Pathfinder Technical Services</span>
        </div>

        {/* Card */}
        <div style={{ background: T.white, borderRadius: 16, boxShadow: T.shadowLg, padding: 40 }}>
          {isSuccess ? (
            <>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#E8F8F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke={T.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 17l7 7L26 10"/>
                </svg>
              </div>

              <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: "0 0 10px" }}>
                {isOffboarding ? "Successfully Disconnected" : "Successfully Connected"}
              </h1>
              <p style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.6, marginBottom: 24 }}>
                {isOffboarding ? (
                  <>Pathfinder Technical Services has been removed from <strong>{clientName}</strong>'s tenant. All admin guests and the cross-tenant access policy have been deleted.</>
                ) : (
                  <><strong>{clientName}</strong> is now connected to Pathfinder Technical Services{template ? ` with the ${template} template` : ""}.</>
                )}
              </p>

              {errors > 0 && (
                <div style={{ background: T.amberBg, borderRadius: T.radiusSm, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: T.amber, fontWeight: 500, fontFamily: T.font }}>
                  {errors} configuration step{errors > 1 ? "s" : ""} had warnings. Pathfinder's team will review and resolve.
                </div>
              )}

              <div style={{ background: T.gray100, borderRadius: T.radiusSm, padding: 20, textAlign: "left", marginBottom: 24 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 12 }}>
                  {isOffboarding ? "What was removed" : "What happens next"}
                </h3>
                <div style={{ display: "grid", gap: 10 }}>
                  {(isOffboarding
                    ? [
                        "All Pathfinder admin guest accounts removed from your tenant",
                        "Cross-tenant access policy deleted",
                        "Pathfinder no longer has any access to your tenant",
                        "Your data and users in your tenant are unchanged",
                      ]
                    : [
                        "Pathfinder's team now has secure admin access to your tenant",
                        "All actions are logged in our audit trail for transparency",
                        "You can contact us any time to review or revoke access",
                        "We'll begin with a health check of your environment",
                      ]
                  ).map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: T.charcoal, fontFamily: T.font, lineHeight: 1.5 }}>
                      <span style={{ color: T.green, flexShrink: 0, marginTop: 1 }}>✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font }}>
                Questions? Reach out at{" "}
                <a href="mailto:support@pathfindtech.com" style={{ color: T.purple, fontWeight: 600, textDecoration: "none" }}>support@pathfindtech.com</a>
              </p>
            </>
          ) : (
            <>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: T.redBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: T.red, fontSize: 28 }}>✕</div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: "0 0 10px" }}>Something Went Wrong</h1>
              <p style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.6, marginBottom: 24 }}>
                The onboarding process encountered an error. Pathfinder's team has been notified.
              </p>
              <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font }}>
                Please contact{" "}
                <a href="mailto:support@pathfindtech.com" style={{ color: T.purple, fontWeight: 600, textDecoration: "none" }}>support@pathfindtech.com</a>
                {" "}for assistance.
              </p>
            </>
          )}
        </div>

        <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.35)", fontFamily: T.font, marginTop: 24 }}>
          You can close this window.
        </p>
      </div>
    </div>
  );
}
